import React, { ComponentType } from "react"
import { compose } from "@reduxjs/toolkit"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { DialogContent } from "@mui/material"

import {
  withModalStep,
  withModalDebt,
  withModalPaymentSchedule,
  withModalSubscriptionEligibility,
} from "hocs/features"
import { useAppDispatch } from "redux/hooks"
import { showAlert, showNotice } from "redux/features/notice/noticeSlice"
import {
  updateDialogStep,
  closeDialog,
} from "redux/features/dialog/dialogSlice"
import {
  StepperComponent as Stepper,
  StepComponent as Step,
  StepContentComponent as StepContent,
} from "components/ui/Stepper"
import { Container, Headline } from "components/PaymentSchedule/elements"
import Back from "components/ui/ModalBack"
import Close from "components/ui/ModalClose"
import Notice from "components/ui/Notice"
import Title from "components/PaymentSchedule/Title"
import Invoices from "components/PaymentSchedule/Invoices"
import Actions from "components/PaymentSchedule/Actions"
import SimpleSign from "components/PaymentSchedule/Promise/SimpleSign"
import Nps from "components/Nps"
import NpsSuccess from "components/Nps/Success"
import {
  PS_PROMISE_STEP_INITIAL,
  PS_PROMISE_STEP_SIMPLE_SIGN,
  PS_PROMISE_STEP_NPS,
  PS_PROMISE_STEP_NPS_SUCCESS,
  PS_PROMISE_STEP_FINAL,
  PS_SIGN_STEP_CHOOSE_PROVIDERS,
} from "config"
import { DebtType, PaymentScheduleType } from "types"

type Props = {
  debt: DebtType
  payment_schedule: PaymentScheduleType
  needSubscription: boolean
  allowSubscribe: boolean
  step: number
}

const PaymentSchedulePromiseComponent = ({
  debt,
  payment_schedule,
  needSubscription,
  step,
}: Props) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const { payment_schedule_invoices } = payment_schedule
  const isSigned = Boolean(payment_schedule?.signed_at)

  const isTitle = ![PS_PROMISE_STEP_NPS, PS_PROMISE_STEP_NPS_SUCCESS].includes(
    step,
  )
  const isBackHidden = isSigned && ![PS_PROMISE_STEP_INITIAL].includes(step)

  const handleClose = () => {
    dispatch(closeDialog())
    navigate("/")
  }

  const handleChooseProvider = () => {
    dispatch(updateDialogStep({ step: PS_SIGN_STEP_CHOOSE_PROVIDERS }))
  }

  const handleSignSuccess = () => {
    dispatch(updateDialogStep({ step: PS_PROMISE_STEP_FINAL }))

    setTimeout(() => {
      dispatch(updateDialogStep({ step: PS_PROMISE_STEP_NPS }))
    }, 5000)

    dispatch(
      showNotice({
        message: t("notice.payment_schedule.promise.success"),
      }),
    )
  }

  const handleSignFailure = () => {
    dispatch(updateDialogStep({ step: PS_PROMISE_STEP_INITIAL }))

    dispatch(
      showAlert({
        message: t("notice.payment_schedule.promise.failure"),
      }),
    )
  }

  const handleNpsCreateSuccess = () => {
    dispatch(updateDialogStep({ step: PS_PROMISE_STEP_NPS_SUCCESS }))
  }

  const handleNpsCreateFailure = () => {
    dispatch(updateDialogStep({ step: PS_PROMISE_STEP_NPS }))
  }

  const handleViewPaymentSchedule = () => {
    navigate(`/debts/${debt.id}/payment_schedules/${payment_schedule.id}`)
  }

  const handleNewPaymentSchedule = () => {
    navigate(`/debts/${debt.id}/payment_schedules/new`)
  }

  const handleBack = () => {
    if (!isSigned) {
      handleNewPaymentSchedule()
    }
  }

  const STEPS = [
    {
      label: PS_PROMISE_STEP_INITIAL,
      component: (
        <>
          <Invoices invoices={payment_schedule_invoices} />
          <Actions
            action="promise"
            isSigned={isSigned}
            needSubscription={needSubscription}
            onSign={() => handleChooseProvider()}
            onDontLike={() => handleNewPaymentSchedule()}
          />
          <Notice>{t("features.payment_schedule.notice.promise")}</Notice>
        </>
      ),
    },
    {
      label: PS_PROMISE_STEP_SIMPLE_SIGN,
      component: (
        <SimpleSign
          paymentScheduleId={payment_schedule.id}
          onFailure={() => handleSignFailure()}
          onSuccess={() => handleSignSuccess()}
        />
      ),
    },
    {
      label: PS_PROMISE_STEP_NPS,
      component: (
        <Nps
          paymentScheduleId={payment_schedule.id}
          onFailure={() => handleNpsCreateSuccess()}
          onSuccess={() => handleNpsCreateFailure()}
        />
      ),
    },
    {
      label: PS_PROMISE_STEP_NPS_SUCCESS,
      component: <NpsSuccess onView={handleViewPaymentSchedule} />,
    },
    {
      label: PS_PROMISE_STEP_FINAL,
      component: <Invoices invoices={payment_schedule_invoices} />,
    },
  ]

  return (
    <DialogContent>
      <Back
        disabled={needSubscription}
        hidden={isBackHidden}
        onClick={handleBack}
      />
      <Close onClick={handleClose} />
      <Container
        container
        direction="column"
        justifyContent="center"
        alignItems="center"
      >
        <Headline>
          {isTitle && (
            <Title reference_number={debt?.reference_number}>
              {t("features.payment_schedule.title")}
            </Title>
          )}
        </Headline>
        <Stepper activeStep={step} orientation="vertical">
          {STEPS.map(({ component }, index) => (
            <Step key={index}>
              <StepContent>{component}</StepContent>
            </Step>
          ))}
        </Stepper>
      </Container>
    </DialogContent>
  )
}

export default compose<ComponentType>(
  withModalStep,
  withModalDebt,
  withModalPaymentSchedule,
  withModalSubscriptionEligibility,
)(PaymentSchedulePromiseComponent)
